'use client';

import { Button } from '@/components/atoms/Button';
import { useCartStore } from '@/features/cart/stores/cartStore';
import { ChatButton } from '@/features/chat/components/atoms/ChatButton';
import apiClient from '@/shared/api/client';
import logger from '@/shared/utils/logger';
import { trackEvent } from '@/shared/utils/mixpanel';
import { cn } from '@/styles/utils';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useFilterStore } from '../../stores/filterStore';
import { useProductStore } from '../../stores/productStore';
import VerticalDivider from '../atoms/VerticalDivider';
import { FilterSection } from '../molecules/FilterSection';
import { SelectedFilterTags } from '../molecules/SelectedFilterTags';
import styles from './ProductPageHeader.module.css';
import { ProductPageSizeSelector } from './ProductPageSizeSelector';

export function PageHeader() {
  const { totalCount, setPageSize, pageSize } = useProductStore();
  const { totalCount: cartTotalCount, fetchCartItems } = useCartStore();
  const { filters, resetFilters, unCheckFilter } = useFilterStore();
  const [banner, setBanner] = useState<string>('');
  const router = useRouter();
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleProductManagementClick = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '판매상품 등록하기' });
    if (!isAuthenticated) {
      router.push('/signin');
      return;
    }
    router.push('/account/product-management');
  };

  const handleCartClick = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '장바구니' });
    if (!isAuthenticated) {
      router.push('/signin');
      return;
    }
    router.push('/cart');
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    fetchCartItems();
  }, [fetchCartItems, isAuthenticated]);

  const handleRemoveFilter = (filterId: string, optionName: string) => {
    logger.info(`handleRemoveFilter: ${filterId}, ${optionName}`);
    unCheckFilter(filterId, optionName);
  };

  useEffect(() => {
    async function fetchBanner() {
      const response = await apiClient.get('/text-banner');

      if (response && response.data && response.data.title) {
        setBanner(response.data.title);
      }
    }
    fetchBanner();
  }, []);

  return (
    <div>
      {banner && (<div className={cn('flex mb-[10px] w-full bg-[#DEE5FB] border-[1px] border-[#BEC8E9] py-[6px]', styles.marqueeContainer)}>
        <div className={styles.marquee}>
          <span>{banner}</span>
        </div>
      </div>)}
      <div className={cn('flex items-center w-full gap-4')}>
        <span className="body-14m text-[#323B49]">{`총 ${totalCount.toLocaleString()}개`}</span>
        <VerticalDivider />
        <ProductPageSizeSelector
          initialValue={pageSize}
          onChange={handlePageSizeChange}
        />
        <VerticalDivider />
        <FilterSection filters={filters} onResetFilters={resetFilters} />

        <SelectedFilterTags
          onChangeCheckedFilter={handleRemoveFilter}
          mode="table"
        />

        <div className="flex ml-auto items-center gap-2">
          {isAuthenticated && (
            <>
              <ChatButton />
              <Button
                variant="outline"
                size="lg"
                className={cn(
                  'bg-white text-(--primary-600) border-1 border-(--primary-600) px-4 py-2',
                )}
                onClick={() => handleProductManagementClick()}
              >
                판매상품 등록하기
              </Button>
            </>
          )}

          <Button
            variant="outline"
            size="lg"
            className={cn(
              'bg-[#C21E0C] text-white gap-3',
              cartTotalCount > 0 ? 'pl-6 pr-4' : '',
            )}
            onClick={() => handleCartClick()}
          >
            장바구니
            {cartTotalCount > 0 && (
              <span className="body-14m w-5 h-5 rounded-full bg-white text-[#C21E0C] border-0 flex items-center justify-center">
                {cartTotalCount > 99 ? '99' : cartTotalCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
